'use client';

import { cn } from '@/lib/utils';
import { useAnalytics } from '@/hooks/use-analytics';
import { haptic } from '@/lib/haptic';
import { Bell, BellMinus, BellPlus } from 'lucide-react';
import { Tip } from '@/components/ui/tooltip';
import { useT } from '@/components/i18n-provider';

export type FollowButtonSize = 'xs' | 'sm' | 'md' | 'lg';
export type FollowButtonVariant = 'default' | 'agent' | 'human' | 'channel' | 'menu';

interface FollowButtonProps {
  isFollowing: boolean;
  loading?: boolean;
  size?: FollowButtonSize;
  variant?: FollowButtonVariant;
  className?: string;
  /** Return `true` when the follow/unfollow request succeeded so analytics can record it. */
  onClick: (e: React.MouseEvent) => void | boolean | Promise<boolean | void | undefined>;
  /** Target id for analytics (user, channel, or tag id). */
  followTargetId?: string;
  followTargetType?: 'user' | 'channel' | 'tag';
  disabled?: boolean;
}

export function FollowButton({
  isFollowing,
  loading,
  className,
  onClick,
  followTargetId,
  followTargetType,
  disabled,
}: FollowButtonProps) {
  const t = useT();
  const { trackFollow, trackUnfollow } = useAnalytics();
  const handleClick = (e: React.MouseEvent) => {
    haptic('medium');
    const wasFollowing = isFollowing;
    const out = onClick(e);
    if (followTargetId === undefined || followTargetType === undefined) return;
    void Promise.resolve(out).then((result) => {
      if (result !== true) return;
      if (wasFollowing) trackUnfollow(followTargetType, followTargetId);
      else trackFollow(followTargetType, followTargetId);
    });
  };

  return (
    <Tip label={isFollowing ? t('actions.unfollow') : t('actions.follow')}>
    <button
      onClick={handleClick}
      disabled={disabled || loading}
      className={cn(
        'shrink-0 p-1.5 rounded-md transition-colors cursor-pointer group/follow',
        'text-muted-foreground hover:bg-accent hover:text-foreground',
        className,
      )}
    >
      {loading ? (
        <span className="h-4.5 w-4.5 border-2 border-current border-t-transparent rounded-full animate-spin block" />
      ) : isFollowing ? (
        <>
          <Bell className="h-4.5 w-4.5 fill-current group-hover/follow:hidden" />
          <BellMinus className="h-4.5 w-4.5 hidden group-hover/follow:block" />
        </>
      ) : (
        <>
          <Bell className="h-4.5 w-4.5 group-hover/follow:hidden" />
          <BellPlus className="h-4.5 w-4.5 hidden group-hover/follow:block" />
        </>
      )}
    </button>
    </Tip>
  );
}
